import { useState, Fragment } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import {
  ResumeEditorStore,
  DownloadDocumentHandler,
  selectors,
  actions,
} from 'builder/modules/resumeEditor'
import { FORMATS, ResumeUploadStatus, ResumeValidationStatus } from 'builder/modules/constants'
import { GeneralEditorStore } from 'builder/modules/generalEditor'
import Menu from 'builder/features/navigation/components/SideMenu/Menu'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useConfig } from 'builder/hooks/useConfig'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import {
  selectors as careerProfileSelectors,
  actions as careerProfileActions,
} from 'builder/modules/careerProfile'
import { selectors as uiSelectors } from 'builder/modules/ui'
import { MobileDrawerMenu } from 'builder/features/navigation/components/MobileMenu/MobileMoreMenu/MobileMoreMenu'
import { useScrollState } from 'builder/hooks/useScrollState'
import loaderImage from 'images/builder/resume-editor/editor-loader.svg'
import { AutoTailoringModal } from '../AutoTailoring/AutoTailoringModal'
import FillResumeModal, { FillResumeByUrl } from '../FillResumeModal'
import CreateResumeWithAI from '../FillResumeModal/CreateResumeWithAI'
import UseExample from '../FillResumeModal/UseExample'
import { PreventOriginalResumeEditModal } from '../AutoTailoring/PreventOriginalResumeEditModal'
import { CreateResumeWithLinkedin } from '../FillResumeModal/CreateResumeWithLinkedin/CreateResumeWithLinkedin'
import { AnchorContextProvider } from '../Anchor'
import { BeforeAutoTailoringKeywordsModal } from '../AutoTailoring/BeforeAutoTailoring'
import { TopNavBarDesktop } from './TopNavBar/TopNavBarDesktop'
import { NavigationHeaderTablet } from './TopNavBar/NavigationHeaderTablet'
import { StickyTabs } from './TopNavBar/StickyTabs'
import { AIReviewContent, DesignContent, EditContent, TailorContent } from './TabContents'
import { TabContent as TabContentComponent } from './TabContent'
import {
  EditorPanel,
  LoaderContainer,
  Loader,
  MenuContainer,
  EditorPanelWithTabs,
  MobileTabsWrapper,
} from './styles'
import { PreviewResume } from './PreviewResume/PreviewResume'
import { ElementHighlight } from './ElementHighlight'
import { useStepByStepFeature } from './StepByStepFlow/hooks/useStepByStepFeature'
import { EditorUnblockedSnackbar } from './EditorUnblockedSnackbar'
import { ConversationalBuilderComponent } from './AiWriter/styles'
import { TailoringFromPublicJobBoardHeader } from './TailoringFromPublicJobBoard'
import { SaveIndicator } from './SaveIndicator/SaveIndicator'

type Props = {
  resumeState: ResumeEditorStore
  editorState: GeneralEditorStore
  showResumeDistributionModal?: (format: FORMATS) => void
  handleDownloadDocument: DownloadDocumentHandler
  handleShareDocument?: () => void
}

export const ResumeEditorWithTabs = (props: Props) => {
  const {
    resumeState,
    editorState,
    showResumeDistributionModal,
    handleDownloadDocument,
    handleShareDocument,
  } = props
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const autoTailoringOpenedModal = useTypedSelector(selectors.autoTailoringOpenedModal)
  const isConversationalBuilderOpen = useTypedSelector(uiSelectors.isConversationalBuilderOpen)
  const { isRbpStepFlowActive } = useStepByStepFeature()
  const features = useConfig()?.features
  const { showAIResumeModal } = useTypedSelector(state => state.resumeEditor.aiResume)
  const { showUseExampleModal } = useTypedSelector(state => state.resumeEditor.useExample)
  const { showCreateResumeWithLinkedInModal } = useTypedSelector(selectors.createResumeWithLinkedin)
  const dispatch = useDispatch()
  const mediaQueries = useMediaQueries()
  const { isAboveTablet } = mediaQueries
  const isMobileProfileMenuOpen = useTypedSelector(careerProfileSelectors.isMobileProfileMenuOpen)
  const { canScrollTop, canScrollBottom } = useScrollState({ topThreshold: 100 })
  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )

  const { resume, editorPanelType } = resumeState
  const { resumeTemplates, locales } = editorState
  const showPrefillResumeModalProp = resume?.showPrefillResumeModal
  const showKeywordsSelectionModal =
    autoTailoringOpenedModal === 'keywords_selection' && autoTailoringFromPublicJobBoard.enabled
  const showAutoTailoringModal = autoTailoringOpenedModal === 'auto_tailoring'
  const showTabletNavBar =
    !isAboveTablet && !isRbpStepFlowActive && editorPanelType !== 'customization'
  const showPrefillResumeModal =
    showPrefillResumeModalProp &&
    (features?.aiResumeDraft || features?.startWithExamples || features?.uploadResume)

  const handleSidebarToggle = (isOpen: boolean) => {
    setIsSidebarOpen(isOpen)
  }

  const handleMobileMenu = (isOpen: boolean) => {
    dispatch(careerProfileActions.setMobileProfileMenu(isOpen))
  }

  const handleLocaleSelect = (locale: string) => {
    dispatch(actions.updateSimpleField({ name: 'locale', value: locale, debounce: true }))
    trackInternalEvent('change_resume_locale', { locale })
  }

  const handlePrefillModalClose = () => {
    dispatch(actions.setResumeValidationStatus(ResumeValidationStatus.notStarted))
    dispatch(actions.setResumeUploadStatus(ResumeUploadStatus.notStarted))
    dispatch(
      actions.updateSimpleField({
        name: 'showPrefillResumeModal',
        value: false,
        debounce: false,
      }),
    )
  }

  if (!resume || resumeTemplates.length === 0) {
    return (
      <EditorPanel>
        <LoaderContainer>
          <Loader src={loaderImage} />
        </LoaderContainer>
      </EditorPanel>
    )
  }

  const editContent = (
    <EditContent
      resumeState={resumeState}
      showResumeDistributionModal={showResumeDistributionModal}
      handleDownloadDocument={handleDownloadDocument}
      handleShareDocument={handleShareDocument}
      canScrollBottom={canScrollBottom}
    />
  )

  const designContent = (
    <DesignContent
      resume={resume}
      resumeState={resumeState}
      canScrollTop={canScrollTop}
      isRbpStepFlowActive={isRbpStepFlowActive}
      handleShareDocument={props.handleShareDocument}
      handleDownloadDocument={props.handleDownloadDocument}
      showResumeDistributionModal={props.showResumeDistributionModal}
    />
  )

  const previewContent = (
    <PreviewResume resume={resume} hideCustomizeButton={editorPanelType === 'customization'} />
  )

  const TabContent = {
    editor: { leftColumn: editContent, rightColumn: null },
    customization: {
      leftColumn: designContent,
      rightColumn: null,
    },
    tailoring: { leftColumn: <TailorContent />, rightColumn: null },
    ai_review: { leftColumn: editContent, rightColumn: <AIReviewContent /> },
  }

  const MobileTabContent = {
    editor: editContent,
    customization: designContent,
    tailoring: <TailorContent canScrollBottom={canScrollBottom} />,
    ai_review: <AIReviewContent canScrollBottom={canScrollBottom} />,
  }

  return (
    <AnchorContextProvider type="native">
      <EditorPanelWithTabs data-testid="editor-panel">
        {isAboveTablet &&
          (autoTailoringFromPublicJobBoard.enabled ? (
            <TailoringFromPublicJobBoardHeader isNewBuilderEnabled />
          ) : (
            <TopNavBarDesktop
              resumeState={resumeState}
              isSidebarOpen={isSidebarOpen}
              onSidebarToggle={handleSidebarToggle}
              handleDownloadDocument={props.handleDownloadDocument}
              showResumeDistributionModal={props.showResumeDistributionModal}
              handleShareDocument={props.handleShareDocument}
              isRbpStepFlowActive={isRbpStepFlowActive}
              locales={locales}
              onLocaleSelect={handleLocaleSelect}
              isConversationalBuilderOpen={isConversationalBuilderOpen}
            />
          ))}

        {showTabletNavBar &&
          (autoTailoringFromPublicJobBoard.enabled ? (
            <TailoringFromPublicJobBoardHeader isNewBuilderEnabled />
          ) : (
            <NavigationHeaderTablet
              resumeState={resumeState}
              onSidebarToggle={handleSidebarToggle}
              locales={locales}
              onLocaleSelect={handleLocaleSelect}
              isSidebarOpen={isSidebarOpen}
            />
          ))}

        {isSidebarOpen && (
          <MenuContainer>
            <Menu isOpen={isSidebarOpen} width="236px" />
          </MenuContainer>
        )}

        {isAboveTablet && (
          <TabContentComponent
            leftColumn={
              <Fragment>
                {TabContent[editorPanelType]?.leftColumn}
                {isConversationalBuilderOpen && <ConversationalBuilderComponent />}
              </Fragment>
            }
            rightColumn={TabContent[editorPanelType]?.rightColumn || previewContent}
          />
        )}

        {!isAboveTablet && (
          <MobileTabsWrapper>
            {editorPanelType !== 'customization' && (
              <StickyTabs
                resumeState={resumeState}
                isRbpStepFlowActive={isRbpStepFlowActive}
                canScrollTop={canScrollTop}
              />
            )}
            {MobileTabContent[editorPanelType]}
            {isConversationalBuilderOpen && <ConversationalBuilderComponent />}
          </MobileTabsWrapper>
        )}

        {showPrefillResumeModal && <FillResumeModal onClose={handlePrefillModalClose} />}
        {showAIResumeModal && <CreateResumeWithAI />}
        {showUseExampleModal && <UseExample />}
        {showCreateResumeWithLinkedInModal && <CreateResumeWithLinkedin />}
        {showKeywordsSelectionModal && (
          <BeforeAutoTailoringKeywordsModal
            isConfirmClosingDisabled={autoTailoringFromPublicJobBoard.enabled}
          />
        )}
        {showAutoTailoringModal && <AutoTailoringModal />}
        <PreventOriginalResumeEditModal />
        <ElementHighlight />
        <EditorUnblockedSnackbar />
        <FillResumeByUrl />
        <MobileDrawerMenu open={isMobileProfileMenuOpen} setOpen={handleMobileMenu} />
        {isAboveTablet && <SaveIndicator isSyncing={resumeState.isSyncing} />}
      </EditorPanelWithTabs>
    </AnchorContextProvider>
  )
}
